/**
 * @file 编辑直播任务全屏弹窗
 */

import { Moment } from '@seiue/moment'
import { Button, State, Toast } from '@seiue/ui'
import { omit, pick } from '@seiue/util'
import { Form } from 'antd'
import moment from 'moment'
import { FC, useEffect, useRef, useState } from 'react'

import {
  TaskStatusEnum,
  TaskTypeEnum,
  activityApi,
  liveApi,
} from 'packages/sdks-next/growp'

import { CommonBasicInfoRef, SubmitValues } from '@/components/CommonBasicInfo'
import { ContentCard } from '@/components/ContentCard'
import { ModalLayout } from '@/components/ModalLayout'
import { TaskHeaderLeft } from '@/components/TaskHeaderLeft'

import { EditTaskModalProps } from '../types'

import { BasicInfo } from './BasicInfo'

export type LiveSubmitValues =
  | SubmitValues
  | {
      liveLink: string
      liveTime: {
        liveDate?: Moment
        liveTimes?: Moment[]
      }
    }

/**
 * 编辑直播任务弹窗
 *
 * @param param0 - 参数
 * @param param0.onClose - 关闭弹窗
 * @param param0.taskId - 任务 ID
 * @param param0.activityId - 活动 ID
 * @param param0.activityTaskOptions - 活动任务选项
 * @returns 组件
 */
export const EditLiveTaskModal: FC<EditTaskModalProps> = ({
  onClose,
  activityId,
  taskId,
  activityTaskOptions,
}) => {
  const isEdit = !!taskId

  const [form] = Form.useForm()
  const [title, setTitle] = useState<string>('')
  const [commonBasicInfo, setCommonBasicInfo] = useState<LiveSubmitValues>({})
  const basicInfoRef = useRef<CommonBasicInfoRef>(null)

  const { data: editTask, loading } = liveApi.useLoadLiveActivityTask(
    {
      id: taskId || 0,
      query: {
        isForeground: false,
      },
    },
    { disable: !taskId }
  )

  const filterTaskOptions =
    activityTaskOptions?.filter((o) => o.value !== taskId) || []

  const { api: createActivityTask } = liveApi.useCreateLiveActivityTask({
    reload: [activityApi.queryTasks, activityApi.loadActivity],
  })

  const { api: updateActivityTask } = liveApi.useUpdateLiveActivityTask({
    reload: [activityApi.queryTasks, activityApi.loadActivity],
  })

  useEffect(() => {
    form.resetFields()

    const setting = editTask?.setting || {}
    const liveTime = {
      liveDate: setting.date !== undefined ? moment(setting.date) : undefined,
      liveTimes:
        setting.startTime !== undefined
          ? [moment(setting.startTime), moment(setting.endTime)]
          : undefined,
    }

    form.setFieldsValue({
      ...editTask,
      liveLink: setting.url,
      liveTime: liveTime,
    })
    setTitle(editTask?.name || '')

    setCommonBasicInfo({
      ...pick({ ...editTask }, [
        'endAt',
        'adminIds',
        'timedStartAt',
        'prevTaskIds',
        'setting',
      ]),
      startAt: editTask
        ? editTask.startAt
        : moment().format('YYYY-MM-DD HH:mm:ss'),
      isShow: editTask?.isShow !== undefined ? editTask.isShow : true,
      liveLink: setting.url,
      liveTime: liveTime,
    })
  }, [editTask, form])

  if (loading) return <State.Loading />
  return (
    <ModalLayout
      title={
        <TaskHeaderLeft
          title={title || '未命名直播'}
          onClose={onClose}
        />
      }
      headerRight={
        <>
          <Button
            type="primary"
            onClick={async () => {
              form.submit()
              const commonRefInfo = basicInfoRef.current?.getBasicInfo()
              form.validateFields().then(async (values) => {
                const liveInfo = pick(values, ['liveLink', 'liveTime'])
                const liveDate = liveInfo.liveTime.liveDate as Moment
                const liveStartTime = liveInfo.liveTime.liveTimes[0] as Moment
                const liveEndTime = liveInfo.liveTime.liveTimes[1] as Moment
                const data = {
                  name: values.name ?? ('' as string),
                  ...omit(commonRefInfo ?? {}, ['liveLink', 'liveTime']),
                  activityId,
                  type: TaskTypeEnum.Live,
                  status: TaskStatusEnum.Published,
                  setting: {
                    url: liveInfo.liveLink,
                    date: liveDate.format('YYYY-MM-DD'),
                    startTime: liveStartTime.format('YYYY-MM-DD HH:mm:ss'),
                    endTime: liveEndTime.format('YYYY-MM-DD HH:mm:ss'),
                  },
                }
                if (isEdit) {
                  await updateActivityTask({
                    id: taskId,
                    payload: data,
                  })
                } else {
                  await createActivityTask(data)
                }

                Toast.success(isEdit ? '更新任务成功' : '保存任务成功')

                onClose()
              })
            }}
          >
            {isEdit ? '更新' : '确认'}
          </Button>
        </>
      }
      content={
        <ContentCard
          style={{ minHeight: 200 }}
          contentStyle={{
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
            padding: '24px 32px',
          }}
        >
          <BasicInfo
            setTitle={setTitle}
            form={form}
            commonBasicInfo={commonBasicInfo as SubmitValues}
            basicInfoRef={basicInfoRef}
            activityTaskOptions={filterTaskOptions}
            openTypeDisabled={editTask?.isSubmit}
          />
        </ContentCard>
      }
    />
  )
}
